import React, { useEffect, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, Percent } from "lucide-react";

const DAYS = [
    { idx: 0, label: "Lun" }, { idx: 1, label: "Mar" }, { idx: 2, label: "Mer" },
    { idx: 3, label: "Gio" }, { idx: 4, label: "Ven" }, { idx: 5, label: "Sab" }, { idx: 6, label: "Dom" },
];

export default function AdminDiscounts() {
    const [list, setList] = useState([]);
    const [slots, setSlots] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = () => Promise.all([
        api.get("/admin/discounts").then((r) => setList(r.data)),
        api.get("/admin/slots").then((r) => setSlots(r.data)),
    ]);
    useEffect(() => { load(); }, []);

    const del = async (id) => {
        if (!window.confirm("Eliminare questo sconto?")) return;
        try { await api.delete(`/admin/discounts/${id}`); toast.success("Eliminato"); load(); }
        catch { toast.error("Errore"); }
    };
    const toggle = async (d) => {
        try { await api.put(`/admin/discounts/${d.discount_id}`, { active: !d.active }); load(); }
        catch { toast.error("Errore"); }
    };

    return (
        <div className="flex min-h-screen bg-ink" data-testid="admin-discounts-page">
            <AdminSidebar />
            <main className="flex-1 p-10">
                <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <div className="text-gold text-xs tracking-[0.4em] uppercase">Gestione</div>
                        <h1 className="font-serif text-5xl text-white mt-2">Sconti</h1>
                        <p className="text-gray-400 text-sm mt-2">Crea promozioni: percentuale applicata al totale del pre-ordine per giorni e orari scelti.</p>
                    </div>
                    <button onClick={() => setCreating(true)} data-testid="add-discount-btn"
                        className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-5 py-3 rounded-sm hover:bg-gold-light">
                        <Plus size={16} /> Nuovo sconto
                    </button>
                </div>

                {list.length === 0 ? (
                    <div className="bg-ink-surface border border-white/10 rounded-sm p-12 text-center">
                        <Percent size={32} className="text-gray-600 mx-auto mb-3" strokeWidth={1} />
                        <p className="text-gray-400 font-serif text-xl mb-4">Nessuno sconto attivo.</p>
                        <button onClick={() => setCreating(true)} className="bg-gold text-black px-6 py-3 rounded-sm font-semibold">
                            Crea il primo
                        </button>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {list.map((d) => (
                            <div key={d.discount_id} className={`bg-ink-surface border rounded-sm p-6 ${d.active ? "border-gold/30" : "border-white/10 opacity-60"}`} data-testid={`discount-${d.discount_id}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <h3 className="font-serif text-xl text-white">{d.name}</h3>
                                        <div className="font-serif text-4xl text-gold mt-2">−{d.percentage}%</div>
                                    </div>
                                    <div className="flex gap-1">
                                        <button onClick={() => setEditing(d)} className="p-2 text-gray-400 hover:text-gold" data-testid={`edit-${d.discount_id}`}>
                                            <Edit3 size={13} strokeWidth={1.5} />
                                        </button>
                                        <button onClick={() => del(d.discount_id)} className="p-2 text-gray-400 hover:text-red-400" data-testid={`delete-${d.discount_id}`}>
                                            <Trash2 size={13} strokeWidth={1.5} />
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-4 pt-4 border-t border-white/5 space-y-2">
                                    <div className="text-[10px] uppercase tracking-widest text-gray-500">Giorni</div>
                                    <div className="flex flex-wrap gap-1">
                                        {(d.days_of_week.length === 0 ? DAYS : DAYS.filter(x => d.days_of_week.includes(x.idx))).map((day) => (
                                            <span key={day.idx} className="text-[10px] px-2 py-0.5 bg-gold/10 text-gold rounded-sm border border-gold/20">{day.label}</span>
                                        ))}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-widest text-gray-500 pt-2">Orari</div>
                                    <div className="text-xs text-gray-400">
                                        {d.slot_ids.length === 0 ? "Tutti" : d.slot_ids.length + " slot"}
                                    </div>
                                    {(d.min_customer_points ?? 0) > 0 && (
                                        <div className="pt-2" data-testid={`points-badge-${d.discount_id}`}>
                                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 bg-gold/10 text-gold rounded-sm border border-gold/30 uppercase tracking-widest">
                                                Da {d.min_customer_points} punti
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <button onClick={() => toggle(d)} className={`mt-4 w-full text-xs py-2 border rounded-sm uppercase tracking-widest ${d.active ? "border-amber-500/30 text-amber-400 hover:bg-amber-500/10" : "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"}`} data-testid={`toggle-${d.discount_id}`}>
                                    {d.active ? "Disattiva" : "Attiva"}
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {(editing || creating) && (
                    <DiscountDialog discount={editing} slots={slots}
                        onClose={() => { setEditing(null); setCreating(false); }}
                        onSaved={() => { load(); setEditing(null); setCreating(false); }} />
                )}
            </main>
        </div>
    );
}

function DiscountDialog({ discount, slots, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: discount?.name || "",
        percentage: discount?.percentage || 10,
        days_of_week: discount?.days_of_week || [],
        slot_ids: discount?.slot_ids || [],
        min_customer_points: discount?.min_customer_points ?? 0,
        active: discount?.active ?? true,
    });

    const toggleDay = (idx) => {
        setForm((f) => ({
            ...f,
            days_of_week: f.days_of_week.includes(idx)
                ? f.days_of_week.filter((x) => x !== idx)
                : [...f.days_of_week, idx],
        }));
    };
    const toggleSlot = (id) => {
        setForm((f) => ({
            ...f,
            slot_ids: f.slot_ids.includes(id)
                ? f.slot_ids.filter((x) => x !== id)
                : [...f.slot_ids, id],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...form,
                percentage: parseInt(form.percentage, 10),
                min_customer_points: Math.max(0, parseInt(form.min_customer_points || 0, 10)),
            };
            if (discount) await api.put(`/admin/discounts/${discount.discount_id}`, payload);
            else await api.post("/admin/discounts", payload);
            toast.success("Salvato");
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 overflow-y-auto" onClick={onClose}>
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
                className="bg-ink-surface border border-white/10 rounded-sm p-8 max-w-xl w-full space-y-5 my-8" data-testid="discount-dialog">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-serif text-2xl text-white">{discount ? "Modifica sconto" : "Nuovo sconto"}</h3>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
                </div>

                <Field label="Nome sconto">
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="discount-name-input" className={inputCls} placeholder="Es. Pranzo infrasettimanale" />
                </Field>
                <Field label="Percentuale (%)">
                    <input type="number" min="1" max="100" required value={form.percentage}
                        onChange={(e) => setForm({ ...form, percentage: e.target.value })}
                        data-testid="discount-percent-input" className={inputCls} />
                </Field>

                <Field label="Punti fedeltà richiesti (0 = tutti i clienti)">
                    <input type="number" min="0" value={form.min_customer_points}
                        onChange={(e) => setForm({ ...form, min_customer_points: e.target.value })}
                        data-testid="discount-min-points-input" className={inputCls}
                        placeholder="Es. 30 = solo chi ha almeno 30 punti" />
                    <p className="text-[11px] text-gray-500 mt-2">
                        I clienti accumulano 10 punti per ogni recensione lasciata. Usa questa soglia per premiare i clienti fedeli con sconti maggiori.
                    </p>
                </Field>

                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold mb-2">Giorni applicabili (vuoto = tutti)</div>
                    <div className="flex flex-wrap gap-2">
                        {DAYS.map((d) => (
                            <button type="button" key={d.idx} onClick={() => toggleDay(d.idx)}
                                data-testid={`day-${d.idx}`}
                                className={`px-3 py-2 text-xs uppercase tracking-widest border rounded-sm ${form.days_of_week.includes(d.idx) ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-gray-400 hover:border-white/30"}`}>
                                {d.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold mb-2">Slot orari applicabili (vuoto = tutti)</div>
                    <div className="flex flex-wrap gap-2 max-h-40 overflow-auto no-scrollbar">
                        {slots.map((s) => (
                            <button type="button" key={s.slot_id} onClick={() => toggleSlot(s.slot_id)}
                                data-testid={`slot-${s.slot_id}`}
                                className={`px-3 py-2 text-xs border rounded-sm ${form.slot_ids.includes(s.slot_id) ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-gray-400 hover:border-white/30"}`}>
                                {s.time} · {s.service}
                            </button>
                        ))}
                    </div>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                        data-testid="discount-active-input" className="w-4 h-4 accent-gold" />
                    <span className="text-sm text-gray-300">Attivo</span>
                </label>

                <button type="submit" data-testid="discount-save-btn"
                    className="w-full bg-gold text-black font-semibold py-3 rounded-sm hover:bg-gold-light tracking-widest uppercase text-sm">
                    Salva sconto
                </button>
            </form>
        </div>
    );
}

const inputCls = "w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";
function Field({ label, children }) {
    return <label className="block"><span className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold">{label}</span><div className="mt-2">{children}</div></label>;
}
